import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Eye,
  EyeOff,
  Building,
  User,
  Mail,
  Lock,
  ArrowRight,
  Check,
  Zap,
  Shield,
  Sparkles,
  Crown,
  Rocket,
  Users,
  X,
  CheckCircle,
  Clock,
} from "lucide-react";
import { useApi } from "../../hooks/useApi";
import { useCurrency } from "../../hooks/useCurrency";
import toast from "react-hot-toast";
import { GoogleLogin, useGoogleOneTapLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "../../contexts/AuthContext";
import ReCAPTCHA from "react-google-recaptcha";
import Logo from "../../components/Logo/Logo";
import { openRazorpayCheckout } from "../../utils/razorpayCheckout";
import {
  Modal,
  Card,
  InputNumber,
  Button,
  Badge,
  Typography,
  Space,
  Divider,
  Select,
} from "antd";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { useTheme } from "../../contexts/ThemeContext";
const { Title, Text, Paragraph } = Typography;

const Register = () => {
  const { mode: theme } = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showOrgNameModal, setShowOrgNameModal] = useState(false);
  const [pendingGoogleCredential, setPendingGoogleCredential] = useState(null);
  const [googleUser, setGoogleUser] = useState(null);
  const [invitationData, setInvitationData] = useState(null);
  const [isInvitationValid, setIsInvitationValid] = useState(false);
  const [showPlanSelection, setShowPlanSelection] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [createdUserId, setCreatedUserId] = useState(null);
  const [registrationTokens, setRegistrationTokens] = useState(null); // Store tokens from registration
  const [isFreePlanActive, setIsFreePlanActive] = useState(false); // Track if Free plan is already active
  const [processingPlan, setProcessingPlan] = useState(null); // Track which plan is being processed
  const [memberCount, setMemberCount] = useState(1); // Number of team members (excluding admin)
  const [selectedMonths, setSelectedMonths] = useState(1); // Number of months to pay upfront
  const [totalAmount, setTotalAmount] = useState(0); // Total amount to be paid
  const [couponCode, setCouponCode] = useState(""); // Coupon code entered by user
  const [actualCouponId, setActualCouponId] = useState(null); // Actual coupon ID from Stripe (to use in checkout)
  const [couponValid, setCouponValid] = useState(null); // null = not checked, true = valid, false = invalid
  const [couponVerifying, setCouponVerifying] = useState(false); // Loading state for verification
  const [couponDiscount, setCouponDiscount] = useState(null); // Discount info
  const couponTimeoutRef = useRef(null); // For debouncing coupon verification
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { googleSignup } = useAuth();
  const api = useApi();
  const recaptchaRef = React.useRef(null);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const { formatPrice, getPrice, symbol, currency, resolved: currencyResolved } = useCurrency();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const password = watch("password");

  // Check for invitation parameters on component mount
  useEffect(() => {
    const orgToken = searchParams.get("org");
    const token = searchParams.get("token");

    if (orgToken && token) {
      validateInvitation(orgToken, token);
    }
  }, [searchParams]);

  const validateInvitation = async (orgId, token) => {
    try {
      const response = await api.get(
        `/api/v1/organizations/validate-invitation?org=${orgId}&token=${token}`
      );

      if (response.data.success) {
        setInvitationData(response.data.data);
        setIsInvitationValid(true);
        toast.success("Invitation validated successfully!");
      } else {
        setIsInvitationValid(false);
        toast.error("Invalid or expired invitation link");
      }
    } catch (error) {
      console.error("Invitation validation error:", error);
      setIsInvitationValid(false);
      toast.error("Invalid or expired invitation link");
    }
  };

  const onSubmit = async (data) => {
    if (!recaptchaToken) {
      toast.error("Please complete the reCAPTCHA verification.");
      return;
    }

    setLoading(true);

    try {
      const nameParts = (data.fullName || "").trim().split(/\s+/);
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ") || "";

      let registrationData = {
        firstName,
        lastName,
        email: data.email,
        password: data.password,
      };

      // If this is an invitation-based registration
      if (isInvitationValid && invitationData) {
        registrationData.organizationId = invitationData.organizationId;
        registrationData.invitationToken = invitationData.token;
        registrationData.isInvitationSignup = true;
      } else {
        // Regular registration with new organization
        registrationData.organizationName = data.organizationName;
      }

      const response = await api.post(
        "/api/v1/auth/register",
        { ...registrationData, recaptchaToken }
      );

      if (response.data.success) {
        // Store user ID for plan selection
        setCreatedUserId(response.data.data.user.id);

        // Check if Free plan is already activated from backend
        const freePlanActive = response.data.data.user?.isActivated && 
                               response.data.data.user?.subscriptionStatus === 'trialing';
        setIsFreePlanActive(freePlanActive);

        // Store tokens from registration response (if available)
        if (response.data.data.token && response.data.data.refreshToken) {
          setRegistrationTokens({
            token: response.data.data.token,
            refreshToken: response.data.data.refreshToken
          });
        }

        // Show plan selection modal (skip for invitation signups)
        if (!isInvitationValid) {
          // FREE_PLAN_HIDDEN: don't default-select the hidden Free plan. See FREE_PLAN_TOGGLE.md
          // if (freePlanActive) {
          //   setSelectedPlan("free");
          // }
          setShowPlanSelection(true);
        } else {
          // For invitation signups, they're already activated
          // Store tokens and navigate
          localStorage.setItem("token", response.data.data.token);
          localStorage.setItem("refreshToken", response.data.data.refreshToken);
          toast.success(
            `Account created successfully! Welcome to ${invitationData.organizationName}!`
          );
          navigate("/dashboard");
        }
      } else {
        toast.error(response.data.message || "Registration failed");
      }
    } catch (error) {
      console.error("Registration error:", error);

      // Check for error message in the correct path (error.response.data.error.message)
      let errorMessage =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        "Registration failed. Please try again.";

      // If there are validation details, show the first one or the main message
      if (
        error.response?.data?.error?.details &&
        error.response.data.error.details.length > 0
      ) {
        errorMessage = error.response.data.error.details[0] || errorMessage;
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
      // Reset reCAPTCHA so user can retry
      setRecaptchaToken(null);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    }
  };

  // Use Google One Tap to get user info
  useGoogleOneTapLogin({
    onSuccess: (credentialResponse) => {
      try {
        const decoded = jwtDecode(credentialResponse.credential);
        setGoogleUser({
          name: decoded.name,
          email: decoded.email,
          picture: decoded.picture,
        });
      } catch (error) {
        console.error("Error decoding Google token:", error);
      }
    },
    onError: () => {
      // Silently fail - user can still use regular signup
    },
  });

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      // Decode to get user info for display
      try {
        const decoded = jwtDecode(credentialResponse.credential);
        setGoogleUser({
          name: decoded.name,
          email: decoded.email,
          picture: decoded.picture,
        });
      } catch (error) {
        console.error("Error decoding Google token:", error);
      }

      // Check if user already exists by trying to login first
      try {
        const loginResponse = await api.post("/api/v1/auth/google", {
          idToken: credentialResponse.credential,
          isSignup: false,
        });

        // If login successful, redirect to dashboard
        if (loginResponse.data.success) {
          localStorage.setItem("token", loginResponse.data.data.token);
          localStorage.setItem(
            "refreshToken",
            loginResponse.data.data.refreshToken
          );
          toast.success("Google login successful!");
          navigate("/dashboard");
          return;
        }
      } catch (loginError) {
        // If login fails, user doesn't exist, proceed with signup
        if (
          loginError.response?.data?.error?.message?.includes(
            "Account not found"
          ) ||
          loginError.response?.data?.message?.includes("Account not found")
        ) {
          // Store the credential and show organization name modal
          setPendingGoogleCredential(credentialResponse.credential);
          setShowOrgNameModal(true);
          return;
        }
      }

      toast.error("Google authentication failed. Please try again.");
    } catch (error) {
      console.error("Google signup error:", error);
      toast.error("Google signup failed. Please try again.");
    }
  };

  const handleGoogleError = () => {
    toast.error("Google signup failed. Please try again.");
  };

  const handleGoogleSignup = async (organizationName) => {
    if (!pendingGoogleCredential || !organizationName) {
      toast.error("Missing required information");
      return;
    }

    try {
      setLoading(true);
      // Call Google signup API directly (don't use googleSignup from AuthContext to avoid auto-login)
      const response = await api.post("/api/v1/auth/google", {
        idToken: pendingGoogleCredential,
        isSignup: true,
        organizationName,
      });

      if (response.data.success) {
        const { user, organization } = response.data.data;

        // Store user ID for plan selection (similar to regular registration)
        setCreatedUserId(user.id);

        // Check if Free plan is already activated from backend
        const freePlanActive = user?.isActivated && 
                               user?.subscriptionStatus === 'trialing';
        setIsFreePlanActive(freePlanActive);

        // Store tokens from Google signup response (if available)
        if (response.data.data.token && response.data.data.refreshToken) {
          setRegistrationTokens({
            token: response.data.data.token,
            refreshToken: response.data.data.refreshToken
          });
        }

        // Close org name modal
        setShowOrgNameModal(false);
        setPendingGoogleCredential(null);

        // ALWAYS show plan selection modal for Google signup (same as normal signup)
        // This allows users to see pricing and upgrade options even if Free plan is active
        // FREE_PLAN_HIDDEN: don't default-select the hidden Free plan. See FREE_PLAN_TOGGLE.md
        // if (freePlanActive) {
        //   setSelectedPlan("free");
        // }
        setShowPlanSelection(true);
        toast.success("Organization created! Please select a plan to continue.");
      } else {
        toast.error(response.data.message || "Google signup failed");
      }
    } catch (error) {
      console.error("Google signup error:", error);
      toast.error(
        error.response?.data?.message ||
          "Google signup failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // Calculate total amount when plan, member count, or months changes
  useEffect(() => {
    if (selectedPlan && selectedPlan !== "free") {
      const planAmount = getPrice(selectedPlan);
      const totalSeats = memberCount + 1; // +1 for admin
      const total = totalSeats * planAmount * selectedMonths;
      setTotalAmount(total);
    } else {
      setTotalAmount(0);
    }
  }, [selectedPlan, memberCount, selectedMonths, getPrice]);

  // Re-verify coupon when member count changes
  useEffect(() => {
    if (couponCode && couponCode.trim() !== "" && couponValid !== null) {
      // Re-verify coupon when member count changes
      if (couponTimeoutRef.current) {
        clearTimeout(couponTimeoutRef.current);
      }

      couponTimeoutRef.current = setTimeout(() => {
        verifyCouponCode(couponCode);
      }, 500); // Debounce for 500ms
    }

    return () => {
      if (couponTimeoutRef.current) {
        clearTimeout(couponTimeoutRef.current);
      }
    };
  }, [memberCount]); // Re-verify when member count changes

  // Verify coupon code
  const verifyCouponCode = async (code) => {
    if (!code || code.trim() === "") {
      setActualCouponId(null);
      setCouponValid(null);
      setCouponDiscount(null);
      return;
    }

    setCouponVerifying(true);
    try {
      const totalSeats = memberCount + 1;

      // Try Razorpay coupon first, then Stripe
      let response;
      try {
        response = await api.post("/api/v1/public/razorpay-billing/verify-coupon", {
          couponCode: code.trim(),
          memberCount: totalSeats,
        });
      } catch {
        response = null;
      }

      // If Razorpay didn't find it, try Stripe
      if (!response?.data?.success || !response?.data?.data?.valid) {
        response = await api.post("/api/v1/public/billing/verify-coupon", {
          couponCode: code.trim(),
          memberCount: totalSeats,
        });
      }

      if (response.data.success && response.data.data.valid) {
        setCouponValid(true);
        setCouponDiscount(response.data.data.discount);
        setActualCouponId(response.data.data.couponCode);
        toast.success(`Coupon applied! ${response.data.data.discount}`);
      } else {
        setCouponValid(false);
        setCouponDiscount(null);
        setActualCouponId(null);
        toast.error(response.data.message || "Invalid coupon code");
      }
    } catch (error) {
      setCouponValid(false);
      setCouponDiscount(null);
      setActualCouponId(null);
      toast.error(error.response?.data?.message || "Failed to verify coupon");
    } finally {
      setCouponVerifying(false);
    }
  };

  const handlePlanSelection = async (plan) => {
    // Don't pick a payment gateway until the IP-based currency has actually been
    // resolved. Otherwise the default 'USD' wrongly routes Indian users to Stripe.
    if (!currencyResolved) {
      toast.loading("Detecting your region, please wait...", { duration: 1500 });
      return;
    }

    setSelectedPlan(plan);
    setProcessingPlan(plan); // Set only this plan as processing

    try {
      if (plan === "free") {
        const isIndian = currency === "INR";

        // Non-Indian users with free plan already activated - just redirect
        if (isFreePlanActive && registrationTokens && !isIndian) {
          localStorage.setItem("token", registrationTokens.token);
          localStorage.setItem("refreshToken", registrationTokens.refreshToken);
          api.updateToken(registrationTokens.token);
          setShowPlanSelection(false);
          toast.success("Free plan already active! Redirecting to dashboard...");
          setTimeout(() => { window.location.href = "/dashboard"; }, 100);
          return;
        }

        // For Indian users: always call Razorpay endpoint to set paymentGateway = 'razorpay'
        // For others: call Stripe endpoint
        const freeEndpoint = isIndian
          ? "/api/v1/public/razorpay-billing/user-subscription-checkout"
          : "/api/v1/public/billing/user-subscription-checkout";

        const response = await api.post(freeEndpoint, {
          userId: createdUserId,
          plan: plan,
          ...(actualCouponId && { couponCode: actualCouponId }),
        });

        if (response.data.success) {
          if (response.data.data.token && response.data.data.refreshToken) {
            localStorage.setItem("token", response.data.data.token);
            localStorage.setItem("refreshToken", response.data.data.refreshToken);
            api.updateToken(response.data.data.token);
          } else if (isFreePlanActive && registrationTokens) {
            // Fallback to registration tokens if endpoint didn't return new ones
            localStorage.setItem("token", registrationTokens.token);
            localStorage.setItem("refreshToken", registrationTokens.refreshToken);
            api.updateToken(registrationTokens.token);
          }

          setShowPlanSelection(false);
          toast.success("Free plan activated! Full AI access for 1 month!");
          setTimeout(() => { window.location.href = "/dashboard"; }, 100);
        } else {
          toast.error(response.data.message || "Failed to activate plan");
          setProcessingPlan(null);
        }
      } else {
        // Standard or Premium - validate member count first
        if (memberCount < 1 || memberCount > 1000) {
          toast.error("Please enter a valid number of team members (1-1000)");
          setProcessingPlan(null);
          return;
        }

        // Try Razorpay first (for Indian users), fall back to Stripe.
        // IMPORTANT: only fall back to Stripe when the user is genuinely NOT Indian
        // (backend says GATEWAY_NOT_AVAILABLE). Any other Razorpay failure for an
        // Indian user (bad offer_id, missing plan_id, etc.) must surface as an error
        // instead of silently routing the Indian user to Stripe.
        let gatewayResponse;
        let razorpayUnavailable = false; // true only when backend says this isn't an Indian user
        try {
          gatewayResponse = await api.post(
            "/api/v1/public/razorpay-billing/organization-subscription-checkout",
            {
              userId: createdUserId,
              plan: plan,
              memberCount: memberCount,
              months: selectedMonths,
              ...(couponCode && { couponCode: couponCode.trim() }),
            }
          );
        } catch (rzpError) {
          const errCode = rzpError?.response?.data?.error;
          razorpayUnavailable =
            errCode === "GATEWAY_NOT_AVAILABLE" || errCode === "GATEWAY_DETECTION_FAILED";

          // For Indian users a real Razorpay failure (checkout/plan/offer error) must
          // NOT fall through to Stripe — show the error and stop.
          if (!razorpayUnavailable && currency === "INR") {
            toast.error(
              rzpError?.response?.data?.message ||
                "Could not start Razorpay checkout. Please try again or contact support."
            );
            setProcessingPlan(null);
            return;
          }
          gatewayResponse = null;
        }

        // Razorpay endpoint responded but didn't produce a usable order/subscription.
        // Same rule: an Indian user should not be silently sent to Stripe.
        const rzpUsable =
          gatewayResponse?.data?.success &&
          (gatewayResponse?.data?.data?.subscriptionId || gatewayResponse?.data?.data?.orderId);
        if (!rzpUsable && !razorpayUnavailable && currency === "INR") {
          toast.error(
            gatewayResponse?.data?.message ||
              "Could not start Razorpay checkout. Please try again or contact support."
          );
          setProcessingPlan(null);
          return;
        }

        if (gatewayResponse?.data?.success && (gatewayResponse?.data?.data?.subscriptionId || gatewayResponse?.data?.data?.orderId)) {
          const rzpData = gatewayResponse.data.data;
          openRazorpayCheckout({
            razorpayKeyId: rzpData.razorpayKeyId,
            amount: rzpData.amount,
            currency: rzpData.currency,
            orderId: rzpData.orderId,
            subscriptionId: rzpData.subscriptionId,
            customerName: rzpData.customerName,
            customerEmail: rzpData.customerEmail,
            notes: rzpData.notes,
            description: `TeamGrid ${plan} Plan`,
            onSuccess: async (paymentResponse) => {
              try {
                const verifyRes = await api.post(
                  "/api/v1/public/razorpay-billing/verify-payment",
                  {
                    razorpay_order_id: paymentResponse.razorpay_order_id,
                    razorpay_payment_id: paymentResponse.razorpay_payment_id,
                    razorpay_signature: paymentResponse.razorpay_signature,
                    razorpay_subscription_id: paymentResponse.razorpay_subscription_id,
                    userId: createdUserId,
                    organizationId: rzpData.notes.organizationId,
                    plan: plan,
                    memberCount: memberCount,
                    totalSeats: rzpData.totalSeats,
                    months: selectedMonths,
                    type: "organization_subscription",
                    couponCode: rzpData.appliedCoupon || null,
                  }
                );
                if (verifyRes.data.success) {
                  if (verifyRes.data.data.token) {
                    localStorage.setItem("token", verifyRes.data.data.token);
                    localStorage.setItem("refreshToken", verifyRes.data.data.refreshToken);
                    api.updateToken(verifyRes.data.data.token);
                  }
                  setShowPlanSelection(false);
                  toast.success("Payment successful! Redirecting...");
                  setTimeout(() => { window.location.href = "/dashboard?payment=success"; }, 100);
                } else {
                  toast.error(verifyRes.data.message || "Payment verification failed");
                  setProcessingPlan(null);
                }
              } catch (verifyErr) {
                toast.error("Payment verification failed. Please contact support.");
                setProcessingPlan(null);
              }
            },
            onFailure: (error) => {
              toast.error(error?.description || "Payment failed. Please try again.");
              setProcessingPlan(null);
            },
            onDismiss: () => {
              setProcessingPlan(null);
            },
          });
          return;
        }

        // Stripe flow (default - international users)
        const response = await api.post(
          "/api/v1/public/billing/organization-subscription-checkout",
          {
            userId: createdUserId,
            plan: plan,
            memberCount: memberCount,
            months: selectedMonths,
            ...(couponCode && { couponCode: couponCode.trim() }),
          }
        );

        if (response.data.success) {
          // Redirect to Stripe checkout
          window.location.href = response.data.data.url;
        } else {
          toast.error(
            response.data.message || "Failed to create checkout session"
          );
          setProcessingPlan(null);
        }
      }
    } catch (error) {
      console.error("Plan activation error:", error);
      toast.error(
        error.response?.data?.message || "Failed to process plan selection"
      );
      setProcessingPlan(null); // Reset processing state on error
    }
  };

  const features = [
    {
      text: "Real-time productivity tracking",
      description: "Track your team's activity in real-time.",
      icon: Check,
    },
    {
      text: "AI-powered insights",
      description:
        "Get AI-generated insights to improve your team's performance.",
      icon: Zap,
    },
    {
      text: "5-days free trial",
      description: "Try TeamGrid for 5 days, no credit card required.",
      icon: ArrowRight,
    },
    {
      text: "Team collaboration tools",
      description: "Collaborate with your team using our advanced tools.",
      icon: Building,
    },
    {
      text: "Advanced analytics",
      description:
        "Access detailed analytics to understand your team's productivity.",
      icon: User,
    },
    {
      text: "No credit card required",
      description:
        "Start your free trial without entering your payment details.",
      icon: Mail,
    },
  ];

  const googleSignupRef = React.useRef(null);

  const triggerGoogleSignup = () => {
    const googleButton =
      document.querySelector('[data-testid="google-login-button"]') ||
      document.querySelector('div[role="button"][aria-label*="Google"]') ||
      googleSignupRef.current?.querySelector('div[role="button"]');
    if (googleButton) {
      googleButton.click();
    }
  };

  return (
    <>
      <Helmet>
        <title>Start Free Trial | TeamGrid</title>
        <meta name="description" content="Start your TeamGrid free trial. Sign up in minutes. No credit card required. AI-powered workforce intelligence for your team." />
        <link rel="canonical" href="https://teamgrid.ai/register" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="min-h-screen flex flex-col md:flex-row">
      {/* Left column - Form (white) */}
      <div className="w-full md:w-[40%] min-h-screen bg-white flex flex-col">
        <header className="w-full px-6 py-5 flex items-center justify-start flex-shrink-0">
          <Link to="/" className="flex items-center">
            <Logo size={40} showText={false} variant="dark" />
          </Link>
        </header>

        <div className="flex-1 flex items-center justify-center px-6 py-8 md:py-12">
          <div className="w-full max-w-[400px]">
            {/* <h1 className="text-[28px] md:text-[32px] font-bold text-black mb-2">
              Start your free trial.
            </h1> */}
             <h1 className="text-[28px] md:text-[32px] font-bold text-center text-black mb-2">
              Signup
             </h1>
            <p className="text-sm text-center text-gray-500 mb-8">
              No credit card required. Setup in 5 minutes.
            </p>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit(onSubmit)(e);
              }}
              className="space-y-4"
            >
              <div ref={googleSignupRef} className="hidden">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  useOneTap={false}
                  theme="filled_white"
                  size="large"
                  text="signup_with"
                  shape="rectangular"
                  width="100%"
                />
              </div>

              <button
                type="button"
                onClick={triggerGoogleSignup}
                className="w-full flex items-center justify-center gap-3 bg-white text-gray-800 font-medium py-3 px-4 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                <span>Continue with Google</span>
              </button>

              {/* <button
                type="button"
                className="w-full flex items-center justify-center gap-3 bg-white text-gray-800 font-medium py-3 px-4 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors opacity-90"
                onClick={() => toast("Microsoft sign-in coming soon")}
              >
                <svg className="w-5 h-5" viewBox="0 0 23 23">
                  <path fill="#f35325" d="M1 1h10v10H1z" />
                  <path fill="#81bc06" d="M12 1h10v10H12z" />
                  <path fill="#05a6f0" d="M1 12h10v10H1z" />
                  <path fill="#ffba08" d="M12 12h10v10H12z" />
                </svg>
                <span>Continue with Microsoft</span>
              </button> */}

              <div className="relative py-2">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-3 bg-white text-gray-500">Or continue with email</span>
                </div>
              </div>

              <div>
                <label htmlFor="fullName" className="block text-[14px] font-medium text-[#374151] mb-1.5 leading-auto tracking-normal">Full Name</label>
                <input
                  id="fullName"
                  type="text"
                  {...register("fullName", {
                    required: "Full name is required",
                    minLength: { value: 2, message: "Enter your full name" },
                  })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#18C8C2]/40 focus:border-[#18C8C2]"
                  placeholder="John Doe"
                />
                {errors.fullName && <p className="text-red-500 text-xs mt-1">{errors.fullName.message}</p>}
              </div>

              <div>
                <label htmlFor="email" className="block text-[14px] font-medium text-[#374151] mb-1.5 leading-auto tracking-normal">Work Email</label>
                <input
                  id="email"
                  type="email"
                  {...register("email", {
                    required: "Email is required",
                    pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: "Invalid email address" },
                  })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#18C8C2]/40 focus:border-[#18C8C2]"
                  placeholder="john@company.com"
                />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
              </div>

              {!isInvitationValid ? (
                <div>
                  <label htmlFor="organizationName" className="block text-[14px] font-medium text-[#374151] mb-1.5 leading-auto tracking-normal">Company Name</label>
                  <input
                    id="organizationName"
                    type="text"
                    {...register("organizationName", {
                      required: !isInvitationValid ? "Company name is required" : false,
                      minLength: { value: 2, message: "At least 2 characters" },
                    })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#18C8C2]/40 focus:border-[#18C8C2]"
                    placeholder="Acme Inc."
                  />
                  {errors.organizationName && <p className="text-red-500 text-xs mt-1">{errors.organizationName.message}</p>}
                </div>
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-800">Joining <strong>{invitationData?.organizationName}</strong></p>
                </div>
              )}

              <div>
                <label htmlFor="password" className="block text-[14px] font-medium text-[#374151] mb-1.5 leading-auto tracking-normal">Password</label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    {...register("password", {
                      required: "Password is required",
                      minLength: { value: 8, message: "At least 8 characters" },
                      pattern: {
                        value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
                        message: "Use uppercase, lowercase, number and special character",
                      },
                    })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#18C8C2]/40 focus:border-[#18C8C2] pr-12"
                    placeholder="Create a strong password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>}
              </div>

              <div className="flex items-start gap-2">
                <input
                  id="terms"
                  type="checkbox"
                  {...register("terms", { required: "You must accept the terms and privacy policy" })}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-[#18C8C2] focus:ring-[#18C8C2]"
                />
                <label htmlFor="terms" className="text-sm text-gray-600">
                  I agree to{" "}
                  <Link to="/terms-of-service" className="text-[#18C8C2] hover:underline" target="_blank" rel="noopener noreferrer">Terms of Service</Link>
                  {" "}and{" "}
                  <Link to="/privacy-policy" className="text-[#18C8C2] hover:underline" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>.
                </label>
              </div>
              {errors.terms && <p className="text-red-500 text-xs">{errors.terms.message}</p>}

              <div className="flex justify-center">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                  onChange={(token) => setRecaptchaToken(token)}
                  onExpired={() => setRecaptchaToken(null)}
                />
              </div>

              <button
                type="submit"
                disabled={loading || !recaptchaToken}
                className="w-full py-3.5 px-4 rounded-lg font-semibold text-white bg-[#18C8C2] hover:bg-[#15b3ad] focus:outline-none focus:ring-2 focus:ring-[#18C8C2]/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                    Creating account...
                  </span>
                ) : (
                  // "Create Free Account"
                  "Create Account"
                )}
              </button>

              <p className="text-sm text-gray-600 text-center pt-1">
                Already have an account?{" "}
                <Link to="/login" className="text-[#18C8C2] font-medium hover:underline">
                  Sign in
                </Link>
              </p>
            </form>

            <div className="flex justify-center mt-6">
              <img
                src="/signup_details.webp"
                alt="No credit card, Bank-level security, 5-min setup"
                className="max-w-full h-auto"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Right column - Testimonial (dark) */}
      <div
        className="hidden md:flex md:w-[60%] min-h-screen flex-col justify-between px-8 lg:px-12 py-12 bg-gradient-to-br from-[#0f172a] via-[#0f172a] to-[#1e293b]"
      >
        <div className="flex-1 flex items-center justify-center">
          <div
              className="w-full max-w-[920px] rounded-[16px] bg-white/[0.05] backdrop-blur-sm border border-white/10 shadow-xl"
              style={{ padding: "31.3px 32px 32px 32px" }}
            >
            <p className="text-[16px] font-semibold leading-[24px] text-white/90 mb-6">
              &ldquo;TeamGrid completely transformed how we manage our agile sprints. It&apos;s intuitive, fast, and the<br />support is incredible.&rdquo;
            </p>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#14B8A6] flex items-center justify-center text-white font-bold text-base flex-shrink-0">
                SJ
              </div>
              <div className="min-w-0 flex flex-col gap-0.5">
                <p className="text-white/90 font-semibold text-[16px] leading-[24px]">Surbhi Radiya</p>
                <p className="text-white/70 text-[14px] font-normal leading-[20px]">Product Designer at The Solitude Shop</p>
              </div>
            </div>
          </div>
        </div>
        <p className="text-white/90 text-sm text-left">
          © {new Date().getFullYear()} TeamGrid Inc. All rights reserved.
        </p>
      </div>

      {/* Organization Name Modal for Google Signup */}
      {showOrgNameModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-white mb-4">
              Complete Your Registration
            </h3>
            <p className="text-gray-300 mb-4">
              Please provide your organization name to complete the signup
              process.
            </p>

            <div className="space-y-4">
              <div>
                <label
                  htmlFor="modalOrgName"
                  className="block text-sm font-medium text-gray-300 mb-1"
                >
                  Organization Name
                </label>
                <input
                  id="modalOrgName"
                  type="text"
                  placeholder="Your Company Name"
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-white/20"
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      const orgName = e.target.value.trim();
                      if (orgName) {
                        handleGoogleSignup(orgName);
                      }
                    }
                  }}
                />
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowOrgNameModal(false);
                    setPendingGoogleCredential(null);
                  }}
                  className="flex-1 px-4 py-2 bg-white/10 border border-white/20 text-white rounded-lg hover:bg-white/20 transition-colors backdrop-blur-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const orgName = document
                      .getElementById("modalOrgName")
                      .value.trim();
                    if (orgName) {
                      handleGoogleSignup(orgName);
                    } else {
                      toast.error("Please enter an organization name");
                    }
                  }}
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-[#34A851] to-[#2DBDB8] text-white rounded-lg hover:from-[#2DBDB8] hover:to-[#34A851] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? "Creating..." : "Create Account"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Modal Styles */}
      <style>{`
        .plan-selection-modal .ant-modal-content {
          background: transparent !important;
          box-shadow: none !important;
          padding: 0 !important;
        }
        .plan-selection-modal .ant-modal-body {
          padding: 0 !important;
        }
        .plan-selection-modal .ant-modal-close {
          color: ${
            theme === "dark" ? "rgba(255, 255, 255, 0.8)" : "rgba(0, 0, 0, 0.6)"
          } !important;
          top: 16px !important;
          right: 16px !important;
        }
        .plan-selection-modal .ant-modal-close:hover {
          color: ${theme === "dark" ? "#ffffff" : "#000000"} !important;
          background: ${
            theme === "dark"
              ? "rgba(255, 255, 255, 0.1)"
              : "rgba(0, 0, 0, 0.05)"
          } !important;
        }
        .payment-modal .ant-modal-content {
          background: transparent !important;
          box-shadow: none !important;
          padding: 0 !important;
        }
        .payment-modal .ant-modal-body {
          padding: 0 !important;
        }
        .payment-modal .ant-modal-close {
          color: rgba(255, 255, 255, 0.8) !important;
          top: 16px !important;
          right: 16px !important;
        }
        .payment-modal .ant-modal-close:hover {
          color: #ffffff !important;
          background: rgba(255, 255, 255, 0.1) !important;
        }
        .ant-input-number {
          background: ${
            theme === "dark"
              ? "rgba(255, 255, 255, 0.1)"
              : "rgba(0, 0, 0, 0.05)"
          } !important;
          border: 1px solid ${
            theme === "dark"
              ? "rgba(255, 255, 255, 0.2)"
              : "rgba(0, 0, 0, 0.15)"
          } !important;
        }
        .ant-input-number-input {
          color: ${theme === "dark" ? "#ffffff" : "#000000"} !important;
          background: transparent !important;
        }
        .ant-input-number-handler {
          border-color: ${
            theme === "dark"
              ? "rgba(255, 255, 255, 0.2)"
              : "rgba(0, 0, 0, 0.15)"
          } !important;
        }
        .ant-input-number-handler:hover {
          color: ${theme === "dark" ? "#ffffff" : "#000000"} !important;
        }
        .ant-statistic-content {
          color: ${theme === "dark" ? "#ffffff" : "#000000"} !important;
        }
        .ant-alert {
          border-radius: 12px !important;
        }
        .ant-alert-info {
          background: rgba(59, 130, 246, 0.1) !important;
          border-color: rgba(59, 130, 246, 0.2) !important;
        }
        .ant-alert-warning {
          background: rgba(251, 191, 36, 0.1) !important;
          border-color: rgba(251, 191, 36, 0.2) !important;
        }
        .ant-alert-message {
          color: ${theme === "dark" ? "#ffffff" : "#000000"} !important;
        }
        .ant-alert-description {
          color: ${
            theme === "dark" ? "rgba(255, 255, 255, 0.8)" : "rgba(0, 0, 0, 0.7)"
          } !important;
        }
        .ant-card {
          border-radius: 16px !important;
        }
        .ant-badge {
          color: ${theme === "dark" ? "#ffffff" : "#000000"} !important;
        }
      `}</style>

      {/* Plan Selection Modal - Professional UI with Ant Design & Framer Motion */}
      <Modal
        open={showPlanSelection}
        onCancel={() => {}}
        footer={null}
        closable={false}
        width={1100}
        className="plan-selection-modal"
        styles={{
          mask: {
            backgroundColor:
              theme === "dark" ? "rgba(0, 0, 0, 0.75)" : "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(8px)",
          },
          content: {
            backgroundColor: "transparent",
            boxShadow: "none",
            padding: 0,
          },
        }}
      >
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3 }}
            className={`${
              theme === "dark"
                ? "bg-gradient-to-br from-zinc-900/95 via-zinc-800/95 to-zinc-900/95 border border-white/10"
                : "bg-white border border-gray-200 shadow-xl"
            } rounded-3xl p-8 backdrop-blur-xl`}
          >
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-center mb-10"
            >
              <div className="flex items-center justify-center mb-4">
                <Sparkles className="w-8 h-8 text-blue-400 mr-2" />
                <Title
                  level={2}
                  className={`!mb-0 !text-4xl !font-bold ${
                    theme === "dark" ? "!text-white" : "!text-gray-900"
                  }`}
                >
                  Choose Your Plan
                </Title>
              </div>
              <Paragraph
                className={`!text-lg !mb-0 ${
                  theme === "dark" ? "!text-gray-400" : "!text-gray-600"
                }`}
              >
                Select a plan that fits your team's needs. You can upgrade
                anytime.
              </Paragraph>
            </motion.div>

            {/* Plan Cards */}
            {/* FREE_PLAN_HIDDEN: grid was md:grid-cols-3, set to md:grid-cols-2 while Free card is commented out. See FREE_PLAN_TOGGLE.md */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {/* FREE_PLAN_HIDDEN_START — change `false` to `true` to re-enable the Free Plan card. See FREE_PLAN_TOGGLE.md */}
              {/* Free Plan */}
              {false && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                whileHover={{ scale: 1.02, y: -5 }}
                className="h-full"
              >
                <Card
                  className={`h-full border-2 transition-all cursor-pointer ${
                    selectedPlan === "free"
                      ? `border-emerald-400 ${
                          theme === "dark"
                            ? "bg-emerald-400/10 shadow-lg shadow-emerald-500/20"
                            : "bg-emerald-50 shadow-lg shadow-emerald-500/10"
                        }`
                      : theme === "dark"
                      ? "border-white/20 bg-white/5 hover:border-white/40"
                      : "border-gray-200 bg-white hover:border-gray-300 shadow-md"
                  }`}
                  style={{
                    borderRadius: "16px",
                    backgroundColor:
                      selectedPlan === "free"
                        ? theme === "dark"
                          ? "rgba(16, 185, 129, 0.1)"
                          : "rgba(16, 185, 129, 0.05)"
                        : theme === "dark"
                        ? "rgba(255, 255, 255, 0.05)"
                        : "#ffffff",
                  }}
                  onClick={() => setSelectedPlan("free")}
                >
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 bg-emerald-500/20 rounded-full flex items-center justify-center">
                      <Rocket className="w-8 h-8 text-emerald-400" />
                    </div>
                    <Title
                      level={4}
                      className={`!mb-2 !text-xl ${
                        theme === "dark" ? "!text-white" : "!text-gray-900"
                      }`}
                    >
                      Free Plan
                    </Title>
                    <div className="mb-6">
                      <span
                        className={`text-4xl font-bold ${
                          theme === "dark" ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {formatPrice('free')}
                      </span>
                      <span
                        className={`text-sm ml-1 ${
                          theme === "dark" ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        /month
                      </span>
                    </div>
                    <Space
                      direction="vertical"
                      size="middle"
                      className="w-full mb-6 text-left"
                    >
                      <div
                        className={`flex items-center ${
                          theme === "dark" ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        <Check className="w-4 h-4 text-emerald-400 mr-2 flex-shrink-0" />
                        <span className="text-sm">1 month full AI access</span>
                      </div>
                      {/* <div className={`flex items-center ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        <Check className="w-4 h-4 text-emerald-400 mr-2 flex-shrink-0" />
                        <span className="text-sm">Basic features after trial</span>
                      </div> */}
                      <div
                        className={`flex items-center ${
                          theme === "dark" ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        <Check className="w-4 h-4 text-emerald-400 mr-2 flex-shrink-0" />
                        <span className="text-sm">Up to 5 team members</span>
                      </div>
                      <div
                        className={`flex items-center ${
                          theme === "dark" ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        <Check className="w-4 h-4 text-emerald-400 mr-2 flex-shrink-0" />
                        <span className="text-sm">Org Intelligence</span>
                      </div>
                    </Space>
                    <Button
                      type={selectedPlan === "free" ? "primary" : "default"}
                      size="large"
                      block
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePlanSelection("free");
                      }}
                      disabled={processingPlan !== null}
                      loading={processingPlan === "free"}
                      className={
                        selectedPlan === "free"
                          ? "bg-emerald-500 hover:bg-emerald-600"
                          : theme === "dark"
                          ? "bg-white/10 hover:bg-white/20 border-white/20"
                          : "bg-gray-100 hover:bg-gray-200 border-gray-300 text-gray-800"
                      }
                      style={{ height: "48px", borderRadius: "8px" }}
                    >
                      {processingPlan === "free"
                        ? "Activating..."
                        : isFreePlanActive
                        ? "Continue with Free Plan"
                        : "Activate Free"}
                    </Button>
                  </div>
                </Card>
              </motion.div>
              )}
              {/* FREE_PLAN_HIDDEN_END */}

              {/* Standard Plan */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                whileHover={{ scale: 1.05, y: -8 }}
                className="h-full relative"
              >
                <Badge.Ribbon text="Popular" color="blue" className="!top-2">
                  <Card
                    className={`h-full border-2 transition-all cursor-pointer ${
                      selectedPlan === "standard"
                        ? `border-blue-400 ${
                            theme === "dark"
                              ? "bg-blue-400/10 shadow-xl shadow-blue-500/30"
                              : "bg-blue-50 shadow-xl shadow-blue-500/10"
                          }`
                        : theme === "dark"
                        ? "border-white/20 bg-white/5 hover:border-white/40"
                        : "border-gray-200 bg-white hover:border-gray-300 shadow-md"
                    }`}
                    style={{
                      borderRadius: "16px",
                      backgroundColor:
                        selectedPlan === "standard"
                          ? theme === "dark"
                            ? "rgba(59, 130, 246, 0.1)"
                            : "rgba(59, 130, 246, 0.05)"
                          : theme === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "#ffffff",
                    }}
                    onClick={() => setSelectedPlan("standard")}
                  >
                    <div className="text-center">
                      <div className="w-16 h-16 mx-auto mb-4 bg-blue-500/20 rounded-full flex items-center justify-center">
                        <Zap className="w-8 h-8 text-blue-400" />
                      </div>
                      <Title
                        level={4}
                        className={`!mb-2 !text-xl ${
                          theme === "dark" ? "!text-white" : "!text-gray-900"
                        }`}
                      >
                        Standard
                      </Title>
                      <div className="mb-6">
                        <span
                          className={`text-4xl font-bold ${
                            theme === "dark" ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {formatPrice('standard')}
                        </span>
                        <span
                          className={`text-sm ml-1 ${
                            theme === "dark" ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          /member/month
                        </span>
                      </div>
                      <Space
                        direction="vertical"
                        size="middle"
                        className="w-full mb-6 text-left"
                      >
                        <div
                          className={`flex items-center ${
                            theme === "dark" ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          <Check className="w-4 h-4 text-blue-400 mr-2 flex-shrink-0" />
                          <span className="text-sm">All features</span>
                        </div>
                        <div
                          className={`flex items-center ${
                            theme === "dark" ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          <Check className="w-4 h-4 text-blue-400 mr-2 flex-shrink-0" />
                          <span className="text-sm">
                            Unlimited team members
                          </span>
                        </div>
                        <div
                          className={`flex items-center ${
                            theme === "dark" ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          <Check className="w-4 h-4 text-blue-400 mr-2 flex-shrink-0" />
                          <span className="text-sm">Advanced analytics</span>
                        </div>
                        <div
                          className={`flex items-center ${
                            theme === "dark" ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          <span className="w-4 h-4 mr-2 flex-shrink-0 text-center">
                            ✗
                          </span>
                          <span className="text-sm">No Org Intelligence</span>
                        </div>
                      </Space>

                      {selectedPlan === "standard" && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className={`mb-4 space-y-3 p-4 rounded-lg border ${
                            theme === "dark"
                              ? "bg-white/5 border-white/10"
                              : "bg-gray-50 border-gray-200"
                          }`}
                        >
                          <div>
                            <label
                              className={`block text-sm mb-2 font-medium ${
                                theme === "dark"
                                  ? "text-gray-300"
                                  : "text-gray-700"
                              }`}
                            >
                              Number of Team Members
                            </label>
                            <InputNumber
                              min={1}
                              max={1000}
                              value={memberCount}
                              onChange={(value) => setMemberCount(value || 1)}
                              controls={true}
                              className="w-full"
                              style={{ width: "100%" }}
                              size="large"
                            />
                            <Text
                              className={`text-xs block mt-2 ${
                                theme === "dark"
                                  ? "text-gray-400"
                                  : "text-gray-600"
                              }`}
                            >
                              Admin included (1) + Members ({memberCount}) ={" "}
                              {memberCount + 1} total seats
                            </Text>
                            
                            {/* Select Number of Months */}
                            <div className="mt-4">
                              <label
                                className={`block text-sm mb-2 font-medium ${
                                  theme === "dark"
                                    ? "text-gray-300"
                                    : "text-gray-700"
                                }`}
                              >
                                Select Number of Months
                              </label>
                              <Select
                                value={selectedMonths}
                                onChange={(value) => setSelectedMonths(value)}
                                className="w-full"
                                size="large"
                                options={[
                                  { value: 1, label: "1 Month" },
                                  { value: 2, label: "2 Months" },
                                  { value: 3, label: "3 Months" },
                                  { value: 4, label: "4 Months" },
                                  { value: 5, label: "5 Months" },
                                  { value: 6, label: "6 Months" },
                                  { value: 12, label: "12 Months" },
                                ]}
                              />
                            </div>
                            
                            <div
                              className={`mt-3 p-3 rounded-lg border ${
                                theme === "dark"
                                  ? "bg-blue-500/10 border-blue-500/20"
                                  : "bg-blue-50 border-blue-200"
                              }`}
                            >
                              <Text
                                className={`font-semibold text-base ${
                                  theme === "dark"
                                    ? "text-blue-300"
                                    : "text-blue-600"
                                }`}
                              >
                                Total: {symbol}{totalAmount} for {selectedMonths} {selectedMonths === 1 ? "month" : "months"}
                              </Text>
                            </div>

                            {/* Coupon Code Section */}
                            <div className="mt-4">
                              <label
                                className={`block text-sm font-medium mb-2 ${
                                  theme === "dark"
                                    ? "text-gray-300"
                                    : "text-gray-700"
                                }`}
                              >
                                Have a coupon or promotion code? (Optional)
                              </label>
                              <div className="flex gap-2">
                                <div className="flex-1 relative">
                                  <input
                                    type="text"
                                    value={couponCode}
                                    onChange={(e) => {
                                      const value =
                                        e.target.value.toUpperCase();
                                      setCouponCode(value);

                                      // Clear previous timeout
                                      if (couponTimeoutRef.current) {
                                        clearTimeout(couponTimeoutRef.current);
                                      }

                                      // Verify after user stops typing (debounce)
                                      if (value.trim() === "") {
                                        setActualCouponId(null);
                                        setCouponValid(null);
                                        setCouponDiscount(null);
                                      } else {
                                        // Debounce verification - wait 800ms after user stops typing
                                        couponTimeoutRef.current = setTimeout(
                                          () => {
                                            verifyCouponCode(value);
                                          },
                                          800
                                        );
                                      }
                                    }}
                                    onBlur={() => {
                                      if (couponCode.trim() !== "") {
                                        verifyCouponCode(couponCode);
                                      }
                                    }}
                                    placeholder="Enter coupon or promotion code"
                                    className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 ${
                                      couponValid === true
                                        ? theme === "dark"
                                          ? "bg-green-500/10 border-green-500/50 text-white focus:ring-green-500/50 focus:border-green-500/50"
                                          : "bg-green-50 border-green-500 text-gray-900 focus:ring-green-500 focus:border-green-500"
                                        : couponValid === false
                                        ? theme === "dark"
                                          ? "bg-red-500/10 border-red-500/50 text-white focus:ring-red-500/50 focus:border-red-500/50"
                                          : "bg-red-50 border-red-500 text-gray-900 focus:ring-red-500 focus:border-red-500"
                                        : theme === "dark"
                                        ? "bg-white/5 border-white/20 text-white placeholder-gray-500 focus:ring-blue-500/50 focus:border-blue-500/50"
                                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500"
                                    }`}
                                    style={{ height: "40px" }}
                                    disabled={couponVerifying}
                                  />
                                  {couponVerifying && (
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                                    </div>
                                  )}
                                  {couponValid === true && !couponVerifying && (
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                      <Check className="h-5 w-5 text-green-500" />
                                    </div>
                                  )}
                                  {couponValid === false &&
                                    !couponVerifying && (
                                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                        <X className="h-5 w-5 text-red-500" />
                                      </div>
                                    )}
                                </div>
                                {couponCode && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setCouponCode("");
                                      setActualCouponId(null);
                                      setCouponValid(null);
                                      setCouponDiscount(null);
                                    }}
                                    className={`px-3 py-2 rounded-lg border transition-colors ${
                                      theme === "dark"
                                        ? "bg-white/10 border-white/20 text-gray-300 hover:bg-white/20"
                                        : "bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200"
                                    }`}
                                  >
                                    Clear
                                  </button>
                                )}
                              </div>
                              {couponValid === true && couponDiscount && (
                                <Text
                                  className={`text-sm mt-2 block font-medium ${
                                    theme === "dark"
                                      ? "text-green-400"
                                      : "text-green-600"
                                  }`}
                                >
                                  ✅ Valid! {couponDiscount} will be applied at
                                  checkout
                                </Text>
                              )}
                              {couponValid === false && (
                                <Text
                                  className={`text-sm mt-2 block ${
                                    theme === "dark"
                                      ? "text-red-400"
                                      : "text-red-600"
                                  }`}
                                >
                                  ❌ Invalid coupon code. Please check and try
                                  again.
                                </Text>
                              )}
                              {couponCode &&
                                couponValid === null &&
                                !couponVerifying && (
                                  <Text
                                    className={`text-xs mt-2 block ${
                                      theme === "dark"
                                        ? "text-gray-400"
                                        : "text-gray-600"
                                    }`}
                                  >
                                    💰 Enter coupon code to verify
                                  </Text>
                                )}
                            </div>
                          </div>
                        </motion.div>
                      )}

                      <Button
                        type={
                          selectedPlan === "standard" ? "primary" : "default"
                        }
                        size="large"
                        block
                        onClick={(e) => {
                          e.stopPropagation();
                          if (selectedPlan === "standard") {
                            handlePlanSelection("standard");
                          } else {
                            setSelectedPlan("standard");
                          }
                        }}
                        disabled={processingPlan !== null}
                        loading={processingPlan === "standard"}
                        className={
                          selectedPlan === "standard"
                            ? "bg-blue-600 hover:bg-blue-700"
                            : theme === "dark"
                            ? "bg-white/10 hover:bg-white/20 border-white/20"
                            : "bg-gray-100 hover:bg-gray-200 border-gray-300 text-gray-800"
                        }
                        style={{ height: "48px", borderRadius: "8px" }}
                      >
                        {processingPlan === "standard"
                          ? "Processing..."
                          : selectedPlan === "standard"
                          ? "Proceed to Payment"
                          : "Select Standard"}
                      </Button>
                    </div>
                  </Card>
                </Badge.Ribbon>
              </motion.div>

              {/* Premium Plan */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                whileHover={{ scale: 1.02, y: -5 }}
                className="h-full"
              >
                <Card
                  className={`h-full border-2 transition-all cursor-pointer ${
                    selectedPlan === "premium"
                      ? `border-purple-400 ${
                          theme === "dark"
                            ? "bg-purple-400/10 shadow-lg shadow-purple-500/20"
                            : "bg-purple-50 shadow-lg shadow-purple-500/10"
                        }`
                      : theme === "dark"
                      ? "border-white/20 bg-white/5 hover:border-white/40"
                      : "border-gray-200 bg-white hover:border-gray-300 shadow-md"
                  }`}
                  style={{
                    borderRadius: "16px",
                    backgroundColor:
                      selectedPlan === "premium"
                        ? theme === "dark"
                          ? "rgba(168, 85, 247, 0.1)"
                          : "rgba(168, 85, 247, 0.05)"
                        : theme === "dark"
                        ? "rgba(255, 255, 255, 0.05)"
                        : "#ffffff",
                  }}
                  onClick={() => setSelectedPlan("premium")}
                >
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 bg-purple-500/20 rounded-full flex items-center justify-center">
                      <Crown className="w-8 h-8 text-purple-400" />
                    </div>
                    <Title
                      level={4}
                      className={`!mb-2 !text-xl ${
                        theme === "dark" ? "!text-white" : "!text-gray-900"
                      }`}
                    >
                      Premium
                    </Title>
                    <div className="mb-6">
                      <span
                        className={`text-4xl font-bold ${
                          theme === "dark" ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {formatPrice('premium')}
                      </span>
                      <span
                        className={`text-sm ml-1 ${
                          theme === "dark" ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        /member/month
                      </span>
                    </div>
                    <Space
                      direction="vertical"
                      size="middle"
                      className="w-full mb-6 text-left"
                    >
                      <div
                        className={`flex items-center ${
                          theme === "dark" ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        <Check className="w-4 h-4 text-purple-400 mr-2 flex-shrink-0" />
                        <span className="text-sm">All features</span>
                      </div>
                      <div
                        className={`flex items-center ${
                          theme === "dark" ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        <Check className="w-4 h-4 text-purple-400 mr-2 flex-shrink-0" />
                        <span className="text-sm">Org Intelligence</span>
                      </div>
                      <div
                        className={`flex items-center ${
                          theme === "dark" ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        <Check className="w-4 h-4 text-purple-400 mr-2 flex-shrink-0" />
                        <span className="text-sm">Priority support</span>
                      </div>
                      <div
                        className={`flex items-center ${
                          theme === "dark" ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        <Check className="w-4 h-4 text-purple-400 mr-2 flex-shrink-0" />
                        <span className="text-sm">Advanced AI</span>
                      </div>
                    </Space>

                    {selectedPlan === "premium" && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className={`mb-4 space-y-3 p-4 rounded-lg border ${
                          theme === "dark"
                            ? "bg-white/5 border-white/10"
                            : "bg-gray-50 border-gray-200"
                        }`}
                      >
                        <div>
                          <label
                            className={`block text-sm mb-2 font-medium ${
                              theme === "dark"
                                ? "text-gray-300"
                                : "text-gray-700"
                            }`}
                          >
                            Number of Team Members
                          </label>
                          <InputNumber
                            min={1}
                            max={1000}
                            value={memberCount}
                            onChange={(value) => setMemberCount(value || 1)}
                            controls={true}
                            className="w-full"
                            style={{ width: "100%" }}
                            size="large"
                          />
                          <Text
                            className={`text-xs block mt-2 ${
                              theme === "dark"
                                ? "text-gray-400"
                                : "text-gray-600"
                            }`}
                          >
                            Admin included (1) + Members ({memberCount}) ={" "}
                            {memberCount + 1} total seats
                          </Text>
                          
                          {/* Select Number of Months */}
                          <div className="mt-4">
                            <label
                              className={`block text-sm mb-2 font-medium ${
                                theme === "dark"
                                  ? "text-gray-300"
                                  : "text-gray-700"
                              }`}
                            >
                              Select Number of Months
                            </label>
                            <Select
                              value={selectedMonths}
                              onChange={(value) => setSelectedMonths(value)}
                              className="w-full"
                              size="large"
                              options={[
                                { value: 1, label: "1 Month" },
                                { value: 2, label: "2 Months" },
                                { value: 3, label: "3 Months" },
                                { value: 4, label: "4 Months" },
                                { value: 5, label: "5 Months" },
                                { value: 6, label: "6 Months" },
                                { value: 12, label: "12 Months" },
                              ]}
                            />
                          </div>
                          
                          <div
                            className={`mt-3 p-3 rounded-lg border ${
                              theme === "dark"
                                ? "bg-purple-500/10 border-purple-500/20"
                                : "bg-purple-50 border-purple-200"
                            }`}
                          >
                            <Text
                              className={`font-semibold text-base ${
                                theme === "dark"
                                  ? "text-purple-300"
                                  : "text-purple-600"
                              }`}
                            >
                              Total: {symbol}{totalAmount} for {selectedMonths} {selectedMonths === 1 ? "month" : "months"}
                            </Text>
                          </div>

                          {/* Coupon Code Section */}
                          <div className="mt-4">
                            <label
                              className={`block text-sm font-medium mb-2 ${
                                theme === "dark"
                                  ? "text-gray-300"
                                  : "text-gray-700"
                              }`}
                            >
                              Have a coupon or promotion code? (Optional)
                            </label>
                            <div className="flex gap-2">
                              <div className="flex-1 relative">
                                <input
                                  type="text"
                                  value={couponCode}
                                  onChange={(e) => {
                                    const value = e.target.value.toUpperCase();
                                    setCouponCode(value);

                                    // Clear previous timeout
                                    if (couponTimeoutRef.current) {
                                      clearTimeout(couponTimeoutRef.current);
                                    }

                                    // Verify after user stops typing (debounce)
                                    if (value.trim() === "") {
                                      setActualCouponId(null);
                                      setCouponValid(null);
                                      setCouponDiscount(null);
                                    } else {
                                      // Debounce verification - wait 800ms after user stops typing
                                      couponTimeoutRef.current = setTimeout(
                                        () => {
                                          verifyCouponCode(value);
                                        },
                                        800
                                      );
                                    }
                                  }}
                                  onBlur={() => {
                                    if (couponCode.trim() !== "") {
                                      verifyCouponCode(couponCode);
                                    }
                                  }}
                                  placeholder="Enter coupon or promotion code"
                                  className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 ${
                                    couponValid === true
                                      ? theme === "dark"
                                        ? "bg-green-500/10 border-green-500/50 text-white focus:ring-green-500/50 focus:border-green-500/50"
                                        : "bg-green-50 border-green-500 text-gray-900 focus:ring-green-500 focus:border-green-500"
                                      : couponValid === false
                                      ? theme === "dark"
                                        ? "bg-red-500/10 border-red-500/50 text-white focus:ring-red-500/50 focus:border-red-500/50"
                                        : "bg-red-50 border-red-500 text-gray-900 focus:ring-red-500 focus:border-red-500"
                                      : theme === "dark"
                                      ? "bg-white/5 border-white/20 text-white placeholder-gray-500 focus:ring-blue-500/50 focus:border-blue-500/50"
                                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500"
                                  }`}
                                  style={{ height: "40px" }}
                                  disabled={couponVerifying}
                                />
                                {couponVerifying && (
                                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                                  </div>
                                )}
                                {couponValid === true && !couponVerifying && (
                                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                    <Check className="h-5 w-5 text-green-500" />
                                  </div>
                                )}
                                {couponValid === false && !couponVerifying && (
                                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                    <X className="h-5 w-5 text-red-500" />
                                  </div>
                                )}
                              </div>
                              {couponCode && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setCouponCode("");
                                    setActualCouponId(null);
                                    setCouponValid(null);
                                    setCouponDiscount(null);
                                  }}
                                  className={`px-3 py-2 rounded-lg border transition-colors ${
                                    theme === "dark"
                                      ? "bg-white/10 border-white/20 text-gray-300 hover:bg-white/20"
                                      : "bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200"
                                  }`}
                                >
                                  Clear
                                </button>
                              )}
                            </div>
                            {couponValid === true && couponDiscount && (
                              <Text
                                className={`text-sm mt-2 block font-medium ${
                                  theme === "dark"
                                    ? "text-green-400"
                                    : "text-green-600"
                                }`}
                              >
                                ✅ Valid! {couponDiscount} will be applied at
                                checkout
                              </Text>
                            )}
                            {couponValid === false && (
                              <Text
                                className={`text-sm mt-2 block ${
                                  theme === "dark"
                                    ? "text-red-400"
                                    : "text-red-600"
                                }`}
                              >
                                ❌ Invalid coupon code. Please check and try
                                again.
                              </Text>
                            )}
                            {couponCode &&
                              couponValid === null &&
                              !couponVerifying && (
                                <Text
                                  className={`text-xs mt-2 block ${
                                    theme === "dark"
                                      ? "text-gray-400"
                                      : "text-gray-600"
                                  }`}
                                >
                                  💰 Enter coupon code to verify
                                </Text>
                              )}
                          </div>
                        </div>
                      </motion.div>
                    )}

                    <Button
                      type={selectedPlan === "premium" ? "primary" : "default"}
                      size="large"
                      block
                      onClick={(e) => {
                        e.stopPropagation();
                        if (selectedPlan === "premium") {
                          handlePlanSelection("premium");
                        } else {
                          setSelectedPlan("premium");
                        }
                      }}
                      disabled={processingPlan !== null}
                      loading={processingPlan === "premium"}
                      className={
                        selectedPlan === "premium"
                          ? "bg-purple-600 hover:bg-purple-700"
                          : theme === "dark"
                          ? "bg-white/10 hover:bg-white/20 border-white/20"
                          : "bg-gray-100 hover:bg-gray-200 border-gray-300 text-gray-800"
                      }
                      style={{ height: "48px", borderRadius: "8px" }}
                    >
                      {processingPlan === "premium"
                        ? "Processing..."
                        : selectedPlan === "premium"
                        ? "Proceed to Payment"
                        : "Select Premium"}
                    </Button>
                  </div>
                </Card>
              </motion.div>
            </div>

            {/* Footer */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-center"
            >
              <Text
                className={`text-sm ${
                  theme === "dark" ? "text-gray-400" : "text-gray-600"
                }`}
              >
                💡 You can upgrade or downgrade your plan anytime from the
                settings
              </Text>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </Modal>
    </div>
    </>
  );
};

export default Register;
